import { useQuery } from '@tanstack/react-query';
import http from 'helpers/requests';
import queryClient from '../../../queryClient';
import { useEmbedConfig } from 'modules/Embed/useEmbedConfig';
import { isPublicCatalogPath } from 'modules/PublicCatalog/hooks/useFetchPublicCatalogShare';
// Item 16C (fixpack 2026-07-23): reused directly, not replicated — routes.js
// has no import back into CnGoods (verified: its only deps are
// Auth/redirectUrl.js and its own planAccess.js, neither of which touches
// CnGoods), so this is a one-directional dependency, not a cycle.
import { isSupplierPublicPath } from 'modules/FreeSupplier/routes';

// Partner iframe embeds and the unauthenticated CBAM Portal page/widget render
// without a Clerk user, so none of them can hit /cngoods/cbam. These surfaces
// fall back to the static
// snapshot at /embed-data/cngoods.json (public EU Commission reference
// material that doesn't change between releases).
async function fetchEmbedCnGoodsCbam() {
  const response = await fetch('/embed-data/cngoods.json');
  if (!response.ok) {
    throw new Error(`Failed to load embed cngoods data (${response.status})`);
  }
  const data = await response.json();
  return Array.isArray(data) ? data : data.goods;
}

async function fetchApiCnGoodsCbam() {
  const response = await http.get('/cngoods/cbam');
  return response.data?.goods;
}

export const useFetchCnGoodsCbam = () => {
  const { isEmbed } = useEmbedConfig();
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  const isPublicCatalog = isPublicCatalogPath(pathname);
  const isSupplierPublic = isSupplierPublicPath(pathname);
  const useStatic = isEmbed || isPublicCatalog || isSupplierPublic;

  return useQuery({
    queryKey: ['cngoods-cbam', useStatic ? 'static' : 'api'],
    queryFn: () => (useStatic ? fetchEmbedCnGoodsCbam() : fetchApiCnGoodsCbam()),
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
  });
};

// Awaitable variant for authenticated flows (e.g. emissions report
// generation) that need the goods catalog resolved before proceeding. Joins
// any request the hook already has in flight, or serves the fresh cache.
export const ensureCnGoodsCbam = () =>
  queryClient.fetchQuery({
    queryKey: ['cngoods-cbam', 'api'],
    queryFn: fetchApiCnGoodsCbam,
    staleTime: 30 * 60 * 1000,
  });
